import Grid from '@mui/material/Grid';
import {
    Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    FormControlLabel, IconButton, List, ListItemButton, ListItemText, MenuItem, Paper, Stack, Switch, SvgIcon, TextField, Tooltip, Typography
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { unstable_useBlocker as useBlocker, useNavigate, useParams } from 'react-router-dom';
import { del, get, put } from 'aws-amplify/api';
import { toast } from 'react-toastify';
import { AudioFile, AudioFileCategory, AudioFileType, AudioFileVersion } from '@aviation/cabinapp-cabincast-sdk';
import PageHeader from '../../../common/Components/PageHeader';
import BoxHeader from '../../../common/Components/BoxHeader';
import { Status, useFetch } from '../../../hooks/useFetch';
import AudioFileUpload from './AudioFileUpload';
import ConditionSetsEditor, { IConditionSetDraft, fromDrafts, toDrafts } from './ConditionSetsEditor';
import AudioFileVersionUploadDialog from './AudioFileVersionUploadDialog';
import AudioFileHistoryDialog from './AudioFileHistoryDialog';
import AudioFileFlightsDialog from './AudioFileFlightsDialog';
import { ReactComponent as PlayIcon } from '../../../icons/play.svg';
import { ReactComponent as StopIcon } from '../../../icons/play-stop.svg';
import { ReactComponent as UpdateIcon } from '../../../icons/sync.svg';
import { ReactComponent as TrashIcon } from '../../../icons/trash.svg';
import { CABINCAST, CABINCAST_AUDIOFILES } from '../../../common/paths';
import { AUDIO_FILE_CATEGORIES, AUDIO_FILE_LANGUAGES, DEFAULT_LANGUAGE, formatDuration, uploadAudioFileVersion } from './AudioFile';

const languageLabel = (code?: string) => {
    const language = AUDIO_FILE_LANGUAGES.find(l => l.code === code?.toUpperCase());
    return language ? `${language.label} (${language.code})` : (code ?? '-');
};

function AudioFileDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [refreshCounter, setRefreshCounter] = useState(0);
    const { status, data } = useFetch<AudioFile>(`/api/audiofiles/${id}`, undefined, refreshCounter, true);

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [type, setType] = useState<AudioFileType>('announcement');
    const [category, setCategory] = useState<AudioFileCategory>('safety');
    const [enabled, setEnabled] = useState(true);
    const [validFrom, setValidFrom] = useState('');
    const [validTo, setValidTo] = useState('');
    const [keywords, setKeywords] = useState<string[]>([]);
    const [conditionSets, setConditionSets] = useState<IConditionSetDraft[]>([]);
    const [touched, setTouched] = useState(false);
    const [saving, setSaving] = useState(false);

    const [playingId, setPlayingId] = useState<string | undefined>(undefined);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const playbackRequestRef = useRef(0);
    const [loadingId, setLoadingId] = useState<string | undefined>(undefined);
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [versionToDelete, setVersionToDelete] = useState<AudioFileVersion | undefined>(undefined);
    const [deleting, setDeleting] = useState(false);
    const updateInputRef = useRef<HTMLInputElement>(null);
    const versionToUpdateRef = useRef<AudioFileVersion | undefined>(undefined);
    const [updatingId, setUpdatingId] = useState<string | undefined>(undefined);
    const [deleteFileDialogOpen, setDeleteFileDialogOpen] = useState(false);
    const [deletingFile, setDeletingFile] = useState(false);
    const [fileDeleted, setFileDeleted] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [flightsOpen, setFlightsOpen] = useState(false);

    // Initialise the form whenever (new) data arrives.
    useEffect(() => {
        if (!data) return;
        setTitle(data.Title ?? '');
        setDescription(data.Description ?? '');
        setType(data.Type ?? 'announcement');
        setCategory(data.Category ?? 'safety');
        setEnabled(data.Enabled ?? true);
        setValidFrom(data.ValidFrom ?? '');
        setValidTo(data.ValidTo ?? '');
        setKeywords(data.Keywords ?? []);
        setConditionSets(toDrafts(data.Conditions));
        setTouched(false);
    }, [data]);

    const stopPlayback = () => {
        playbackRequestRef.current++; // invalidates pending URL requests
        setLoadingId(undefined);
        audioRef.current?.pause();
        audioRef.current = null;
        setPlayingId(undefined);
    };

    useEffect(() => stopPlayback, []);

    /** Fetches a short-lived (180 s) signed URL for the MP3 file of the given version. */
    const fetchPlaybackUrl = async (versionId: string): Promise<string> => {
        const response = await get({ path: `/api/audiofiles/file/${versionId}`, apiName: 'api' }).response;
        const body = await response.body.json() as any;
        const url = typeof body === 'string' ? body : (body?.Url ?? body?.url);
        if (!url) throw new Error('No signed URL received');
        return url;
    };

    const togglePlayback = async (version: AudioFileVersion) => {
        if (playingId === version.Id) {
            stopPlayback();
            return;
        }
        stopPlayback();

        const requestId = ++playbackRequestRef.current;
        const fail = () => {
            if (playbackRequestRef.current !== requestId) return;
            toast.error('The audio file could not be played.');
            setPlayingId(undefined);
            setLoadingId(undefined);
        };

        setLoadingId(version.Id);
        try {
            const url = await fetchPlaybackUrl(version.Id!);
            if (playbackRequestRef.current !== requestId) return; // superseded by another click or unmount

            const audio = new Audio(url);
            audio.onended = () => setPlayingId(undefined);
            audio.onerror = fail;
            audioRef.current = audio;
            setLoadingId(undefined);
            setPlayingId(version.Id);
            await audio.play();
        } catch (e) {
            console.error(e);
            fail();
        }
    };

    const handleTypeChange = (value: AudioFileType) => {
        setType(value);
        setCategory(value === 'music' ? 'music' : (category === 'music' ? 'safety' : category));
    };

    const titleMissing = title.trim() === '';
    const dateInvalid = !!validFrom && !!validTo && validTo < validFrom;
    const conditionsInvalid = conditionSets.some(set => set.Conditions.length === 0 || set.Conditions.some(c => c.Value.trim() === ''));
    const canSave = !titleMissing && !dateInvalid && !conditionsInvalid && !saving;

    /** Saves the form; `newVersions` are appended to the existing versions. Returns whether the save succeeded. */
    const handleSave = async (newVersions: AudioFileVersion[] = [], removedVersionIds: string[] = []): Promise<boolean> => {
        setTouched(true);
        if (!canSave || !data) {
            if (newVersions.length > 0) toast.error('Please fix the metadata errors before uploading.');
            return false;
        }

        setSaving(true);
        try {
            const audioFile: AudioFile = {
                ...data,
                Title: title.trim(),
                Description: description.trim() || undefined,
                Type: type,
                Category: type === 'music' ? 'music' : category,
                Enabled: enabled,
                ValidFrom: validFrom || undefined,
                ValidTo: validTo || undefined,
                Keywords: keywords,
                Conditions: fromDrafts(conditionSets),
            };
            // The playback URLs are only meaningful for reading.
            const existing = (audioFile.Versions ?? []).filter(v => !removedVersionIds.includes(v.Id!));
            audioFile.Versions = [...existing, ...newVersions];
            audioFile.Languages = audioFile.Versions.map(v => v.Language!);
            await put({ path: '/api/audiofiles/', apiName: 'api', options: { body: audioFile as any } }).response;
            toast.success('Audio file saved successfully.');
            setRefreshCounter(c => c + 1);
            return true;
        } catch (e) {
            console.error(e);
            toast.error('An error occurred while saving the audio file.');
            return false;
        } finally {
            setSaving(false);
        }
    };

    const handleVersionsUploaded = async (newVersions: AudioFileVersion[]) => {
        if (await handleSave(newVersions)) {
            setUploadDialogOpen(false);
            setSelectedFiles([]);
        }
    };

    const handleDeleteFile = async () => {
        setDeletingFile(true);
        try {
            stopPlayback();
            await del({ path: `/api/audiofiles/${id}`, apiName: 'api' }).response;
            toast.success('Audio file deleted successfully.');
            setDeleteFileDialogOpen(false);
            setFileDeleted(true);
        } catch (e) {
            console.error(e);
            toast.error('An error occurred while deleting the audio file.');
        } finally {
            setDeletingFile(false);
        }
    };

    // Navigate only after the unsaved-changes blocker has been released by the re-render.
    useEffect(() => {
        if (fileDeleted) navigate(`/${CABINCAST}/${CABINCAST_AUDIOFILES}`);
    }, [fileDeleted, navigate]);

    const deleteVersionRequest = async (versionId: string) => {
        await del({ path: `/api/audiofiles/file/${versionId}`, apiName: 'api' }).response;
    };

    const handleUpdateClick = (version: AudioFileVersion) => {
        versionToUpdateRef.current = version;
        updateInputRef.current?.click();
    };

    /** Uploads the new file, saves the audio file with the new version and then deletes the previous version. */
    const handleUpdateFileSelected = async (file?: File) => {
        const oldVersion = versionToUpdateRef.current;
        versionToUpdateRef.current = undefined;
        if (!file || !oldVersion || !data?.Id) return;

        const isMp3 = file.type === 'audio/mpeg' || file.name.toLowerCase().endsWith('.mp3');
        if (!isMp3) {
            toast.error('Only MP3 files are supported.');
            return;
        }

        setUpdatingId(oldVersion.Id);
        try {
            if (playingId === oldVersion.Id) stopPlayback();
            const newVersion = await uploadAudioFileVersion(data.Id, file, oldVersion.Language!);

            // Save first: the audio file then always references an existing file. handleSave reports its own errors.
            if (!await handleSave([newVersion], [oldVersion.Id!])) return;

            // The previous version is no longer referenced by the audio file, so drop it from the list right away.
            setRemovedVersionIds(ids => [...ids, oldVersion.Id!]);

            try {
                await deleteVersionRequest(oldVersion.Id!);
            } catch (e) {
                console.error(e);
                toast.warning('The version was updated, but the previous file could not be removed.');
            }
            // Reload after the delete, the refresh triggered by the save may have returned the old version.
            setRefreshCounter(c => c + 1);
        } catch (e) {
            console.error(e);
            toast.error('An error occurred while updating the version.');
        } finally {
            setUpdatingId(undefined);
        }
    };

    const handleDelete = async () => {
        if (!versionToDelete) return;
        setDeleting(true);
        try {
            if (playingId === versionToDelete.Id) stopPlayback();
            await deleteVersionRequest(versionToDelete.Id!);
            toast.success('Version deleted successfully.');
            setVersionToDelete(undefined);
            setRefreshCounter(c => c + 1);
        } catch (e) {
            console.error(e);
            toast.error('An error occurred while deleting the version.');
        } finally {
            setDeleting(false);
        }
    };

    // Versions replaced during this visit: hidden immediately, the (re)fetch may still return them for a moment.
    const [removedVersionIds, setRemovedVersionIds] = useState<string[]>([]);

    // English first, then alphabetically by language name.
    const versions = useMemo(() => {
        const sortKey = (v: AudioFileVersion) => v.Language?.toUpperCase() === DEFAULT_LANGUAGE ? '' : languageLabel(v.Language).toLowerCase();
        return (data?.Versions ?? [])
            .filter(v => !removedVersionIds.includes(v.Id!))
            .sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
    }, [data, removedVersionIds]);

    const isDirty = useMemo(() => {
        if (!data) return false;
        const original = data.Keywords ?? [];
        return title !== (data.Title ?? '')
            || description !== (data.Description ?? '')
            || type !== (data.Type ?? 'announcement')
            || (type === 'music' ? 'music' : category) !== (data.Category ?? 'safety')
            || enabled !== (data.Enabled ?? true)
            || validFrom !== (data.ValidFrom ?? '')
            || validTo !== (data.ValidTo ?? '')
            || keywords.length !== original.length
            || keywords.some((k, i) => k !== original[i])
            || JSON.stringify(fromDrafts(conditionSets)) !== JSON.stringify(fromDrafts(toDrafts(data.Conditions)));
    }, [data, title, description, type, category, enabled, validFrom, validTo, keywords, conditionSets]);

    const handleDiscard = () => {
        if (!data) return;
        setTitle(data.Title ?? '');
        setDescription(data.Description ?? '');
        setType(data.Type ?? 'announcement');
        setCategory(data.Category ?? 'safety');
        setEnabled(data.Enabled ?? true);
        setValidFrom(data.ValidFrom ?? '');
        setValidTo(data.ValidTo ?? '');
        setKeywords(data.Keywords ?? []);
        setConditionSets(toDrafts(data.Conditions));
        setTouched(false);
    };

    // Ask before leaving the page (in-app navigation) while there are unsaved changes.
    const blocker = useBlocker(isDirty && !saving && !fileDeleted);

    // ... and before closing/reloading the browser tab.
    useEffect(() => {
        if (!isDirty) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);
    const canDelete = versions.length > 1;

    if (status === Status.Idle || (status === Status.Fetching && !data)) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (status === Status.Error || !data) {
        return <Typography color='error'>The audio file could not be loaded.</Typography>;
    }

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <PageHeader title={`Audio file: ${data.Title ?? ''}`}>
                    <Stack direction='row' spacing={1}>
                        <Button variant='contained' color='error' onClick={() => setDeleteFileDialogOpen(true)} disabled={saving || deletingFile}>
                            Delete
                        </Button>
                        <Button variant='contained' onClick={() => handleSave()} disabled={saving || !isDirty}
                            startIcon={saving ? <CircularProgress size={16} color='inherit' /> : undefined}>Save</Button>
                    </Stack>
                </PageHeader>
            </Grid>

            <Grid item xs={12} md={8} lg={9}>
                <Paper sx={{ p: 2 }} elevation={3}>
                    <BoxHeader title='Metadata' />
                    <Stack spacing={2}>
                        <TextField label='Title' required size='small' value={title} onChange={e => setTitle(e.target.value)}
                            error={touched && titleMissing} helperText={touched && titleMissing ? 'Title is required' : undefined} />
                        <TextField label='Description' size='small' multiline minRows={5} value={description}
                            onChange={e => setDescription(e.target.value)} />
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <TextField select fullWidth label='Type' size='small' value={type}
                                onChange={e => handleTypeChange(e.target.value as AudioFileType)}>
                                <MenuItem value='announcement'>Announcement</MenuItem>
                                <MenuItem value='music'>Music</MenuItem>
                            </TextField>
                            <TextField select fullWidth label='Category' size='small' value={type === 'music' ? 'music' : category}
                                disabled={type !== 'announcement'} onChange={e => setCategory(e.target.value as AudioFileCategory)}>
                                {AUDIO_FILE_CATEGORIES.filter(c => type === 'music' ? c.value === 'music' : c.value !== 'music').map(c =>
                                    <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                            </TextField>
                        </Stack>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <TextField fullWidth type='date' label='Valid from' size='small' value={validFrom}
                                onChange={e => setValidFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
                            <TextField fullWidth type='date' label='Valid to' size='small' value={validTo}
                                onChange={e => setValidTo(e.target.value)} InputLabelProps={{ shrink: true }}
                                inputProps={{ min: validFrom || undefined }}
                                error={dateInvalid} helperText={dateInvalid ? '"Valid to" must not be before "Valid from"' : undefined} />
                        </Stack>
                        <Autocomplete
                            multiple freeSolo size='small' options={[] as string[]} value={keywords}
                            onChange={(_, value) => setKeywords(Array.from(new Set(value.map(v => v.trim()).filter(Boolean))))}
                            renderTags={(value, getTagProps) => value.map((option, index) => {
                                const { key, ...tagProps } = getTagProps({ index });
                                return <Chip key={key} size='small' label={option} {...tagProps} />;
                            })}
                            renderInput={(params) => <TextField {...params} label='Keywords' placeholder='Type a keyword and press Enter' />}
                        />
                        <FormControlLabel control={<Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} />} label='Enabled' />
                    </Stack>
                </Paper>

                <Paper sx={{ p: 2, mt: 3 }} elevation={3}>
                    <BoxHeader title='Conditions' subtitle='Conditions within a set must all match (AND). The audio file applies if any set matches (OR).' />
                    <ConditionSetsEditor value={conditionSets} onChange={setConditionSets} showErrors={touched} />
                </Paper>
            </Grid>

            <Grid item xs={12} md={4} lg={3}>
                <Paper sx={{ p: 2, mb: 3 }} elevation={3}>
                    <BoxHeader title='Actions' />
                    <List disablePadding>
                        <ListItemButton onClick={() => setHistoryOpen(true)} sx={{ borderTop: 1, borderColor: 'divider' }}><ListItemText primary='Change History' /></ListItemButton>
                        <ListItemButton onClick={() => setFlightsOpen(true)} sx={{ borderTop: 1, borderColor: 'divider' }}><ListItemText primary='Flights' /></ListItemButton>
                    </List>
                </Paper>
                <Paper sx={{ p: 2 }} elevation={3}>
                    <BoxHeader title='Languages' />
                    <Box>
                        {versions.map(version => {
                            const playing = playingId === version.Id;
                            const isDefault = version.Language?.toUpperCase() === DEFAULT_LANGUAGE;
                            return (
                                <Box key={version.Id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderTop: 1, borderColor: 'divider' }}>
                                    <Tooltip title={playing ? 'Stop' : 'Play'}>
                                        <IconButton color='primary' onClick={() => togglePlayback(version)}>
                                            {loadingId === version.Id
                                                ? <CircularProgress size={20} />
                                                : <SvgIcon component={playing ? StopIcon : PlayIcon} inheritViewBox />}
                                        </IconButton>
                                    </Tooltip>
                                    <Typography variant='body2' sx={{ flexGrow: 1 }}>{languageLabel(version.Language)}</Typography>
                                    <Typography variant='body2' color='text.secondary'>{formatDuration(version.Duration)}</Typography>
                                    <Tooltip title='Replace audio file'>
                                        <span>
                                            <IconButton color='primary' disabled={updatingId !== undefined || saving}
                                                onClick={() => handleUpdateClick(version)}>
                                                {updatingId === version.Id
                                                    ? <CircularProgress size={20} />
                                                    : <SvgIcon component={UpdateIcon} inheritViewBox />}
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    {canDelete && (
                                        <Tooltip title={isDefault ? 'The English version cannot be deleted' : 'Delete'}>
                                            <span>
                                                <IconButton color='error' disabled={isDefault} onClick={() => setVersionToDelete(version)}>
                                                    <SvgIcon component={TrashIcon} inheritViewBox />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </Paper>
                <Paper sx={{ p: 2, mt: 3 }} elevation={3}>
                    <AudioFileUpload onFilesSelected={(files) => { setSelectedFiles(files); setUploadDialogOpen(true); }} />
                </Paper>
            </Grid>

            <input
                ref={updateInputRef}
                type='file'
                accept='.mp3,audio/mpeg'
                hidden
                onChange={(e) => { handleUpdateFileSelected(e.target.files?.[0]); e.target.value = ''; }}
            />

            <AudioFileHistoryDialog open={historyOpen} audioFileId={data.Id!} onClose={() => setHistoryOpen(false)} />
            <AudioFileFlightsDialog open={flightsOpen} audioFileId={data.Id!} onClose={() => setFlightsOpen(false)} />

            <AudioFileVersionUploadDialog
                open={uploadDialogOpen}
                files={selectedFiles}
                audioFileId={data.Id!}
                usedLanguages={versions.map(v => v.Language ?? '')}
                onClose={() => { setUploadDialogOpen(false); setSelectedFiles([]); }}
                onUploaded={handleVersionsUploaded}
            />

            {isDirty && (
                <Grid item xs={12} sx={{ position: 'sticky', bottom: 16, zIndex: 10 }}>
                    <Paper elevation={8} sx={{
                        display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 1.5, borderRadius: 2,
                        backgroundColor: 'primary.main', color: 'common.white'
                    }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: 'warning.light' }} />
                        <Typography sx={{ flexGrow: 1 }}>You have unsaved changes</Typography>
                        <Button variant='outlined' disabled={saving} onClick={handleDiscard}
                            sx={{ color: 'common.white', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: 'common.white' } }}>
                            Discard
                        </Button>
                        <Button variant='contained' disabled={saving} onClick={() => handleSave()}
                            startIcon={saving ? <CircularProgress size={16} color='inherit' /> : undefined}
                            sx={{ backgroundColor: 'common.white', color: 'primary.main', '&:hover': { backgroundColor: 'grey.200' } }}>
                            Save changes
                        </Button>
                    </Paper>
                </Grid>
            )}

            <Dialog open={blocker.state === 'blocked'} onClose={() => blocker.reset?.()}>
                <DialogTitle>Leave this page?</DialogTitle>
                <DialogContent>
                    <DialogContentText>You have unsaved changes. If you leave now, they will be lost.</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => blocker.reset?.()}>Stay</Button>
                    <Button color='error' variant='contained' onClick={() => blocker.proceed?.()}>Leave without saving</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteFileDialogOpen} onClose={deletingFile ? undefined : () => setDeleteFileDialogOpen(false)}>
                <DialogTitle>Delete audio file?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        “{data.Title}” and all of its {versions.length} language version(s) will be deleted permanently. This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteFileDialogOpen(false)} disabled={deletingFile}>Cancel</Button>
                    <Button color='error' variant='contained' onClick={handleDeleteFile} disabled={deletingFile}
                        startIcon={deletingFile ? <CircularProgress size={16} color='inherit' /> : undefined}>Delete</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!versionToDelete} onClose={deleting ? undefined : () => setVersionToDelete(undefined)}>
                <DialogTitle>Delete version?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        The {languageLabel(versionToDelete?.Language)} version will be deleted permanently. This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setVersionToDelete(undefined)} disabled={deleting}>Cancel</Button>
                    <Button color='error' variant='contained' onClick={handleDelete} disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={16} color='inherit' /> : undefined}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
}

export default AudioFileDetails;
